import { useParams } from 'react-router';
import { Footer, TitleBar } from '../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { useUserContext } from '../../../../context/PermissionsContext';
import { SET_PROCESS_AREA_OR_PROCESS_WORKAROUND } from './queries';
import { useMutation } from '@apollo/client';

import { commonColumns } from '../shared/workarounds/columns';
import { EditableColumnType } from '../shared/workarounds';
import { tableSortingMethods } from '../../../../utils';
import {
  WORKAROUND_TYPES,
  WORKAROUND_DISPLAY_LABELS,
} from '../../../../shared/types/business-functions-workarounds';
import { Workaround, WorkaroundType } from './types';
import { useWorkaroundForm } from '../../../../hooks/workarounds/useWorkaroundForm';
import { useProcessAreaOrProcessWorkarounds } from '../process-areas-and-processes/hooks';
import TitleUpdater from '../../../../components/common/TitleUpdater';

const ProcessAreaOrProcessWorkarounds = () => {
  const { locationUUID, processAreaUUID, processUUID } = useParams();

  const title = `${!processUUID ? 'Process Area' : 'Process'} Workarounds`;

  const {
    data,
    loading,
    refetch: refetchWorkarounds,
  } = useProcessAreaOrProcessWorkarounds(
    processAreaUUID,
    processUUID,
    locationUUID,
  );

  const [setWorkaround, { loading: updateLoading }] = useMutation(
    SET_PROCESS_AREA_OR_PROCESS_WORKAROUND,
    {
      onCompleted: async () => await refetchWorkarounds(),
    },
  );

  const { canEdit } = useUserContext();

  const columns: EditableColumnType[] = [
    {
      title: 'IT Application',
      dataIndex: 'itApplicationName',
      key: 'name',
      editable: false,
      defaultSortOrder: 'ascend',
      sorter: (a: Workaround, b: Workaround) =>
        tableSortingMethods.string(a.itApplicationName, b.itApplicationName),
      render: (text: string) => text,
    },
    ...commonColumns,
  ];

  const { formInstance, formFields } = useWorkaroundForm(!processUUID);

  const modifyVariables = ({ currentRecord, values }: ModifyVariablesArgs) => {
    const isInfinite =
      values.runtimeHours ===
      WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.INFINITE];
    const isNoWorkaround =
      values.runtimeHours ===
      WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND];
    const shouldSendRuntimeHours = isInfinite || isNoWorkaround;

    let normalizedRuntimeHours;

    if (!shouldSendRuntimeHours) {
      normalizedRuntimeHours = values.runtimeHours
        ? Number(values.runtimeHours)
        : null;
    }

    return {
      processAreaOrProcessUUID: currentRecord?.processAreaOrProcessId,
      itApplicationUUID: currentRecord?.itApplicationId,
      locationUUID,
      isWorkaroundNeeded: Boolean(values.isWorkaroundNeeded),
      workaroundDefined: values.workaroundDefined,
      runtimeHours: normalizedRuntimeHours,
      maxDataLossHours: values.maxDataLossHours
        ? Number(values.maxDataLossHours)
        : null,
      workaroundDescription: values.workaroundDescription || null,
      type: !processUUID ? WorkaroundType.PROCESS_AREA : WorkaroundType.PROCESS,
      ...(!processUUID && {
        applyToAllProcesses: Boolean(values.applyToAllProcesses),
      }),
    };
  };

  return (
    <div>
      <TitleUpdater title={title} />
      <TitleBar
        title={title}
        subtitle={`${data?.locationName} / ${data?.name} Applications`}
        brief={`Capture and describe how the ${data?.name} can work around a disruption of the following critical applications.`}
      />
      <TableV3
        columns={columns}
        data={data?.workarounds}
        loading={loading || updateLoading}
        title={(currentRecord: Workaround) =>
          `${currentRecord?.itApplicationName} / Edit Workaround`
        }
        formFields={formFields}
        updateGQL={setWorkaround}
        modifyVariables={modifyVariables}
        editable
        editableOptions={{
          update: canEdit,
          delete: false,
        }}
        hideTableHeading
        formInstance={formInstance}
      />
      <Footer
        backPath={`/cyber/location-level/${locationUUID}/process-areas-and-processes/workarounds`}
      />
    </div>
  );
};

export default ProcessAreaOrProcessWorkarounds;
